import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { DomainCardButton } from '../../domain-cards/ui';
import { getFlavorColorClass } from '../../../config/campaignFrameConfig';

/**
 * ResourceHarvestPattern - Pattern for harvesting resources (Beast Feast ingredients, blooms)
 *
 * Supports:
 * - Beast harvest (by HP-based yield table)
 * - Bloom/environmental harvest (by cost + roll table)
 * - Custom ingredient naming from tables
 * - Special ingredient features
 *
 * Config properties used:
 * - triggerLocation: 'combat-end' | 'exploration'
 * - resourceKey: where to store in character.inventory
 * - yieldSource: path to yields array in frameStaticData
 * - rollTableSource: path to roll table in frameStaticData
 * - cost: { type, amount } for bloom-style harvesting
 * - limitPerRest: max uses per rest
 * - flavorConfig: flavor profile definitions
 * - ingredientTablesSource: path to name generation tables
 */
const ResourceHarvestPattern = ({
  frameId,
  mechanicId,
  config,
  character,
  updateCharacter,
  frameStaticData,
  sessionState,
  setSessionState,
  onClose,
}) => {
  // Local state for harvest UI
  const [beastHP, setBeastHP] = useState(0);
  const [harvestedIngredients, setHarvestedIngredients] = useState([]);
  const [isHarvesting, setIsHarvesting] = useState(false);
  const [ingredientSource, setIngredientSource] = useState('');
  const [isSpecialIngredient, setIsSpecialIngredient] = useState(false);
  const [specialFeatureName, setSpecialFeatureName] = useState('');
  const [specialFeatureDescription, setSpecialFeatureDescription] = useState('');

  // Track uses per rest (for bloom-style mechanics)
  const [usesThisRest, setUsesThisRest] = useState(0);

  // Resolve config sources from static data
  const resolveSource = useCallback((sourcePath) => {
    if (!frameStaticData || !sourcePath) return null;
    return sourcePath.split('.').reduce((obj, key) => obj?.[key], frameStaticData);
  }, [frameStaticData]);

  const yields = resolveSource(config.yieldSource);
  const rollTable = resolveSource(config.rollTableSource);
  const ingredientTables = resolveSource(config.ingredientTablesSource);
  const flavorProfiles = frameStaticData?.customMechanics?.cooking?.flavorProfiles || [];

  // Determine harvest type based on config
  const isBloomStyle = !!config.cost && !!rollTable;
  const isBeastStyle = !!yields;

  /**
   * Generate a random ingredient name from tables
   */
  const generateIngredientName = useCallback((isBloom = false) => {
    if (!ingredientTables) return isBloom ? 'Gathered Item' : 'Beast Part';

    const partTable = isBloom
      ? ingredientTables.bloomTypes
      : ingredientTables.beastParts;
    const traitTable = ingredientTables.uniqueTraits;

    if (!partTable) return isBloom ? 'Gathered Item' : 'Beast Part';

    const part = partTable[Math.floor(Math.random() * partTable.length)];
    const trait = Math.random() < 0.3 && traitTable
      ? traitTable[Math.floor(Math.random() * traitTable.length)]
      : null;

    const baseName = isBloom ? part : `Beast ${part}`;
    return trait ? `${baseName} (${trait})` : baseName;
  }, [ingredientTables]);

  /**
   * Handle beast-style harvest (HP-based yield)
   */
  const handleBeastHarvest = () => {
    if (!yields || !flavorProfiles.length) {
      toast.error('Harvest data not available');
      return;
    }

    setIsHarvesting(true);

    try {
      // Find yield rule based on beast HP
      const yieldRule = yields.find(y => beastHP <= y.maxHP) || yields[yields.length - 1];
      const numIngredients = yieldRule?.ingredients || 0;

      if (numIngredients === 0) {
        toast.error('Beast too weak to harvest');
        setIsHarvesting(false);
        return;
      }

      // Generate ingredients with random flavors
      const newIngredients = Array.from({ length: numIngredients }, (_, i) => {
        const numFlavors = Math.random() < 0.7 ? 1 : Math.random() < 0.8 ? 2 : 3;
        const selectedFlavors = [];

        for (let j = 0; j < numFlavors; j++) {
          const flavorProfile = flavorProfiles[Math.floor(Math.random() * flavorProfiles.length)];
          const strength = Math.floor(Math.random() * 3) + 1;

          if (!selectedFlavors.find(f => f.profile === flavorProfile.name)) {
            selectedFlavors.push({ profile: flavorProfile.name, strength });
          }
        }

        return {
          id: `harvest-${Date.now()}-${i}`,
          name: generateIngredientName(false),
          flavors: selectedFlavors,
          source: ingredientSource || 'Unknown Beast',
          ...(isSpecialIngredient && specialFeatureName && {
            isSpecial: true,
            specialFeature: {
              name: specialFeatureName,
              description: specialFeatureDescription,
            },
          }),
        };
      });

      setHarvestedIngredients(newIngredients);
      toast.success(`Harvested ${numIngredients} ingredient${numIngredients > 1 ? 's' : ''}!`);

      // Clear form fields
      setIngredientSource('');
      setIsSpecialIngredient(false);
      setSpecialFeatureName('');
      setSpecialFeatureDescription('');
    } catch (error) {
      console.error('Harvest error:', error);
      toast.error('Error during harvest');
    } finally {
      setIsHarvesting(false);
    }
  };

  /**
   * Handle bloom-style harvest (cost + roll table)
   */
  const handleBloomHarvest = () => {
    if (!rollTable) {
      toast.error('Roll table not available');
      return;
    }

    // Check cost
    if (config.cost) {
      const costType = config.cost.type;
      const costAmount = config.cost.amount;
      const currentValue = character[costType] || 0;

      if (currentValue < costAmount) {
        toast.error(`Not enough ${costType}! Need ${costAmount}.`);
        return;
      }
    }

    // Check limit per rest
    if (config.limitPerRest && usesThisRest >= config.limitPerRest) {
      toast.error('Already gathered maximum for this rest!');
      return;
    }

    setIsHarvesting(true);

    try {
      // Roll d12 for bloom result (or configurable die)
      const roll = Math.ceil(Math.random() * 12);

      // Find matching entry in roll table
      const result = rollTable.find(entry =>
        roll >= entry.range[0] && roll <= entry.range[1]
      );

      if (!result) {
        toast.error('Error rolling for resource');
        setIsHarvesting(false);
        return;
      }

      const newResource = {
        id: `bloom-${Date.now()}`,
        name: generateIngredientName(true),
        flavors: [{ profile: result.profile, strength: result.strength }],
        source: ingredientSource || 'Unknown Location',
        roll,
        ...(isSpecialIngredient && specialFeatureName && {
          isSpecial: true,
          specialFeature: {
            name: specialFeatureName,
            description: specialFeatureDescription,
          },
        }),
      };

      // Apply cost
      if (config.cost) {
        const costType = config.cost.type;
        const costAmount = config.cost.amount;
        updateCharacter({
          ...character,
          [costType]: character[costType] - costAmount,
        });
      }

      setUsesThisRest(prev => prev + 1);
      setHarvestedIngredients([newResource]);
      toast.success(`Gathered ${result.profile} resource! (Rolled ${roll})`);

      // Clear form fields
      setIngredientSource('');
      setIsSpecialIngredient(false);
      setSpecialFeatureName('');
      setSpecialFeatureDescription('');
    } catch (error) {
      console.error('Bloom harvest error:', error);
      toast.error('Error gathering resource');
    } finally {
      setIsHarvesting(false);
    }
  };

  /**
   * Add harvested ingredients to character inventory
   */
  const handleAddToInventory = () => {
    if (harvestedIngredients.length === 0) return;

    const resourceKey = config.resourceKey || 'ingredients';
    const currentInventory = character.inventory?.[resourceKey] || [];
    const newInventory = [...currentInventory, ...harvestedIngredients];

    updateCharacter({
      ...character,
      inventory: {
        ...character.inventory,
        [resourceKey]: newInventory,
      },
    });

    toast.success(`Added ${harvestedIngredients.length} item(s) to inventory!`);
    setHarvestedIngredients([]);

    if (onClose) {
      onClose();
    }
  };

  /**
   * Reset rest tracking
   */
  const handleRest = () => {
    setUsesThisRest(0);
    toast.success('Rest completed! Gathering reset.');
  };

  // Render beast HP options
  const beastHPOptions = Array.from({ length: 200 }, (_, i) => i + 1);

  return (
    <div className="space-y-4">
      {/* Mode-specific instructions (no title - parent handles that) */}
      <div className="text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {isBloomStyle
            ? `Cost: ${config.cost?.amount} ${config.cost?.type}. Limit: ${config.limitPerRest}/rest.`
            : 'Enter beast HP to determine ingredient yield.'}
        </p>
      </div>

      {/* Beast HP Input (for beast-style) */}
      {isBeastStyle && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Beast Max HP
          </label>
          <select
            value={beastHP}
            onChange={(e) => setBeastHP(parseInt(e.target.value))}
            className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
          >
            <option value={0}>Select HP...</option>
            {beastHPOptions.map((hp) => (
              <option key={hp} value={hp}>{hp}</option>
            ))}
          </select>
        </div>
      )}

      {/* Source Input */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Source (optional)
        </label>
        <input
          type="text"
          value={ingredientSource}
          onChange={(e) => setIngredientSource(e.target.value)}
          placeholder={isBloomStyle ? 'Location name...' : 'Beast name...'}
          className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
        />
      </div>

      {/* Special Ingredient Toggle */}
      <div className="space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={isSpecialIngredient}
            onChange={(e) => setIsSpecialIngredient(e.target.checked)}
            className="rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Special Ingredient (has unique feature)
          </span>
        </label>

        {isSpecialIngredient && (
          <div className="ml-6 space-y-2">
            <input
              type="text"
              value={specialFeatureName}
              onChange={(e) => setSpecialFeatureName(e.target.value)}
              placeholder="Feature name..."
              className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
            />
            <textarea
              value={specialFeatureDescription}
              onChange={(e) => setSpecialFeatureDescription(e.target.value)}
              placeholder="Feature description..."
              rows={2}
              className="w-full px-3 py-2 border rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600"
            />
          </div>
        )}
      </div>

      {/* Harvest Button */}
      <div className="space-y-2">
        <DomainCardButton
          onClick={isBloomStyle ? handleBloomHarvest : handleBeastHarvest}
          disabled={
            isHarvesting ||
            (isBeastStyle && beastHP === 0) ||
            (isBloomStyle && config.limitPerRest && usesThisRest >= config.limitPerRest)
          }
          variant={isBloomStyle && config.limitPerRest && usesThisRest >= config.limitPerRest ? 'disabled' : 'primary'}
          className="w-full"
        >
          {isHarvesting ? 'Harvesting...' : (isBloomStyle ? '🌸 Gather' : '🥩 Harvest')}
        </DomainCardButton>

        {/* Status for limited-use mechanics */}
        {isBloomStyle && config.limitPerRest && (
          <div className="flex items-center justify-between text-sm">
            {usesThisRest >= config.limitPerRest ? (
              <>
                <span className="text-amber-600 dark:text-amber-400">
                  Used this rest • Refreshes on rest
                </span>
                <DomainCardButton
                  onClick={handleRest}
                  variant="secondary"
                  className="px-2 py-0.5 text-xs"
                >
                  Reset
                </DomainCardButton>
              </>
            ) : (
              <span className="text-gray-500 dark:text-gray-400">
                {config.limitPerRest - usesThisRest} use{config.limitPerRest - usesThisRest !== 1 ? 's' : ''} remaining this rest
              </span>
            )}
          </div>
        )}
      </div>

      {/* Harvested Items Display */}
      {harvestedIngredients.length > 0 && (
        <div className="mt-4 space-y-2">
          <h4 className="font-medium text-gray-700 dark:text-gray-300">
            Harvested Items:
          </h4>
          <div className="space-y-2 max-h-40 overflow-y-auto">
            {harvestedIngredients.map((ing) => (
              <div
                key={ing.id}
                className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg"
              >
                <div className="font-medium">{ing.name}</div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  From: {ing.source}
                </div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {ing.flavors.map((f, i) => (
                    <span
                      key={i}
                      className={`px-2 py-0.5 text-xs rounded ${getFlavorColorClass(f.profile)}`}
                    >
                      {f.profile} x{f.strength}
                    </span>
                  ))}
                </div>
                {ing.isSpecial && ing.specialFeature && (
                  <div className="mt-1 text-sm text-purple-600 dark:text-purple-400">
                    ✨ {ing.specialFeature.name}: {ing.specialFeature.description}
                  </div>
                )}
              </div>
            ))}
          </div>

          <DomainCardButton
            onClick={handleAddToInventory}
            variant="activeSuccess"
            className="w-full"
          >
            Add to Inventory
          </DomainCardButton>
        </div>
      )}
    </div>
  );
};

export default ResourceHarvestPattern;
